/**
 * examples.js — Respuestas hardcodeadas para TESTEAR la visualización del chat.
 *
 * Uso:
 *   - Cambiá `ACTIVE_EXAMPLE` por la key de la respuesta que querés ver.
 *   - Con un valor válido, el chat muestra esa propuesta por defecto, sin que
 *     haga falta mandar ningún mensaje.
 *   - Poné `ACTIVE_EXAMPLE = null` para desactivar y volver al flujo normal.
 *
 * Cada entrada es una respuesta completa de `POST /chat/message` con
 * `response_type: "action_proposal"`, tal como la documenta el contrato.
 *
 * ⚠️ Esto es solo para desarrollo/QA visual. Antes de mergear a producción,
 * dejá `ACTIVE_EXAMPLE = null` (o eliminá la integración en MapAIChatbot).
 */

// 👇 Elegí acá qué respuesta visualizar:
// 'add_displays' | 'delete_displays' | 'update_campaign_budget' |
// 'update_campaign_dates' | 'change_campaign_type' | 'change_client' |
// 'change_brand' | 'change_external_dsp' | 'suggest_reset' | null
export const ACTIVE_EXAMPLE = null

export const EXAMPLES = {
  /* ---------------- add_displays ---------------- */
  add_displays: {
    response_type: 'action_proposal',
    message: 'Encontré 3 pantallas en Mendoza. ¿Las agrego a la campaña?',
    proposal_id: 'prop_example_add',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'add_displays',
      payload: {
        display_ids: [112832, 114078, 114079],
        reason: 'Pantallas outdoor grandes en Mendoza Capital',
        preview: [
          {
            id: 112832,
            name: 'Acceso Este. Ingreso a ciudad.',
            formatted_address: 'Av. José Vicente Zapata 137',
            administrative_area_level_1: 'Mendoza',
            administrative_area_level_2: 'Guaymallén',
            price_currency: 'ARS',
            picture_url: 'https://picsum.photos/seed/112832/80',
            purchase_type: 'programmatic',
            cpmi: 1200.0,
          },
          {
            id: 114078,
            name: 'El juli tele 2',
            formatted_address: 'De La Cuesta, Luján de Cuyo',
            administrative_area_level_1: 'Mendoza',
            administrative_area_level_2: 'Las Heras',
            price_currency: 'ARS',
            picture_url: null,
            purchase_type: 'traditional',
            price_per_day: 194.57,
          },
          {
            id: 114079,
            name: 'Shopping Palmares - Hall central',
            formatted_address: 'Panamericana 2650',
            administrative_area_level_1: 'Mendoza',
            administrative_area_level_2: 'Godoy Cruz',
            price_currency: 'ARS',
            picture_url: 'https://picsum.photos/seed/114079/80',
            purchase_type: 'programmatic',
            cpmi: 2100.0,
          },
        ],
      },
    },
  },

  /* ---------------- delete_displays ---------------- */
  delete_displays: {
    response_type: 'action_proposal',
    message: 'Voy a quitar esta pantalla de la campaña. ¿Confirmás?',
    proposal_id: 'prop_example_delete',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'delete_displays',
      payload: {
        display_ids: [66071],
        reason: 'El usuario solicitó quitar esta pantalla',
        preview: [
          {
            id: 66071,
            name: 'Test Cont',
            formatted_address: 'Av. Perú 1052',
            administrative_area_level_1: 'Mendoza',
            administrative_area_level_2: 'Capital',
            price_currency: 'ARS',
            picture_url: 'https://picsum.photos/seed/66071/80',
            purchase_type: 'programmatic',
            cpmi: 3502.35,
          },
        ],
      },
    },
  },

  /* ---------------- update_campaign_budget ---------------- */
  update_campaign_budget: {
    response_type: 'action_proposal',
    message: 'Propongo subir el presupuesto un 20%. ¿Lo aplico?',
    proposal_id: 'prop_example_budget',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'update_campaign_budget',
      payload: {
        current_budget: 500000.0,
        new_budget: 600000.0,
        currency: 'ARS',
        reason: 'Incremento del 20% solicitado por el usuario',
      },
    },
  },

  /* ---------------- update_campaign_dates ---------------- */
  update_campaign_dates: {
    response_type: 'action_proposal',
    message: 'Propongo correr la campaña 10 días. ¿Confirmás las nuevas fechas?',
    proposal_id: 'prop_example_dates',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'update_campaign_dates',
      payload: {
        current_date_from: '2026-06-01',
        current_date_to: '2026-06-21',
        new_date_from: '2026-06-10',
        new_date_to: '2026-06-30',
        reason: 'Correr la campaña 10 días',
        // current/new_budget y currency solo vienen en tradicionales:
        current_budget: 250000.0,
        new_budget: 300000.0,
        currency: 'ARS',
      },
    },
  },

  /* ---------------- change_campaign_type ---------------- */
  change_campaign_type: {
    response_type: 'action_proposal',
    message: 'Propongo cambiar la campaña a tradicional. ¿Confirmás?',
    proposal_id: 'prop_example_type',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'change_campaign_type',
      payload: {
        current_type: 'programmatic',
        new_type: 'traditional',
        current_budget: 250000.0,
        new_budget: 194560.0,
        currency: 'ARS',
      },
    },
  },

  /* ---------------- change_client ---------------- */
  change_client: {
    response_type: 'action_proposal',
    message: 'Propongo cambiar el cliente de la campaña. ¿Confirmás?',
    proposal_id: 'prop_example_client',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'change_client',
      payload: {
        client_id: 4457,
        client_name: 'Coca-Cola',
      },
    },
  },

  /* ---------------- change_brand ---------------- */
  change_brand: {
    response_type: 'action_proposal',
    message: 'Propongo cambiar la marca de la campaña. ¿Confirmás?',
    proposal_id: 'prop_example_brand',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'change_brand',
      payload: {
        brand_id: 6069,
        brand_name: 'Pepsi',
      },
    },
  },

  /* ---------------- change_external_dsp ---------------- */
  change_external_dsp: {
    response_type: 'action_proposal',
    message: 'Propongo cambiar el DSP externo de la campaña. ¿Confirmás?',
    proposal_id: 'prop_example_dsp',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: false,
    proposal: {
      action: 'change_external_dsp',
      payload: {
        dsp_id: 183,
        external_dsp_name: 'Taggify Latinad',
      },
    },
  },

  /* ---------------- suggest_reset ----------------
     Respuesta de texto (sin propuesta) que activa el aviso anticipado de
     reseteo: la conversación se está acercando al corte automático. El front
     muestra la barra "ChatbotResetWarning" con el botón de reinicio. */
  suggest_reset: {
    response_type: 'answer',
    message: 'Listo, apliqué los cambios. La conversación se está poniendo larga; si querés, reiniciá el chat para seguir más rápido.',
    session_id: 123,
    timestamp: '2026-06-04T14:00:00Z',
    suggest_reset: true,
  },
};

export default EXAMPLES;
